import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../Account/Register/Register.style";
import { COLORS } from "../../../../shared/theme/colorConfig";
import { theme } from "../../../../shared/theme/theme";

type HouseholdStatus = "A" | "B" | "C" | "";
type YesNo = "yes" | "no" | "";

export interface StudentDetailsForm {
  householdStatus: HouseholdStatus;
  q1_titleIV: YesNo;
  q2_fosterCare: YesNo;
  q3_jtpa: YesNo;
  q4_singleParent: YesNo;
  q5_marriedJointReturn: YesNo;
  // Forward-looking household question for TAA Supplemental §3
  // (`comp_students_planning`): will anyone become a student in the next 12
  // months? Distinct from current student status (driven by member profiles).
  studentsPlanningToEnroll: YesNo;
  _acknowledgedAt?: number;
}

interface PropertyContact {
  phone?: string;
  email?: string;
  name?: string;
}

interface StudentDetailsProps {
  defaultValues?: StudentDetailsForm;
  onSubmit: (data: StudentDetailsForm) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
  propertyContact?: PropertyContact;
  // Present only in the co-applicant flow — the primary's already-submitted answer.
  primaryStudentDetails?: StudentDetailsForm;
}

export const StudentDetails: React.FC<StudentDetailsProps> = ({
  defaultValues,
  onSubmit,
  onBack,
  isSaving = false,
  propertyContact,
  primaryStudentDetails,
}) => {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(
    !!defaultValues?._acknowledgedAt
  );

  const yesNoSchema = () =>
    yup.string().when("householdStatus", {
      is: "C",
      then: (s) =>
        s
          .oneOf(
            ["yes", "no"],
            t("affordable.application.studentDetails.answerRequired") as string
          )
          .required(
            t("affordable.application.studentDetails.answerRequired") as string
          ),
      otherwise: (s) => s.notRequired(),
    });

  const schema = yup.object({
    householdStatus: yup
      .string()
      .oneOf(
        ["A", "B", "C"],
        t("affordable.application.studentDetails.selectOption") as string
      )
      .required(
        t("affordable.application.studentDetails.selectOption") as string
      ),
    q1_titleIV: yesNoSchema(),
    q2_fosterCare: yesNoSchema(),
    q3_jtpa: yesNoSchema(),
    q4_singleParent: yesNoSchema(),
    q5_marriedJointReturn: yesNoSchema(),
    studentsPlanningToEnroll: yup
      .string()
      .oneOf(
        ["yes", "no"],
        t("affordable.application.studentDetails.answerRequired") as string
      )
      .required(
        t("affordable.application.studentDetails.answerRequired") as string
      ),
  });

  const EXEMPTION_QUESTIONS: {
    key: keyof Pick<
      StudentDetailsForm,
      | "q1_titleIV"
      | "q2_fosterCare"
      | "q3_jtpa"
      | "q4_singleParent"
      | "q5_marriedJointReturn"
    >;
    number: number;
    textKey: string;
  }[] = [
    { key: "q1_titleIV", number: 1, textKey: "q1" },
    { key: "q2_fosterCare", number: 2, textKey: "q2" },
    { key: "q3_jtpa", number: 3, textKey: "q3" },
    { key: "q4_singleParent", number: 4, textKey: "q4" },
    { key: "q5_marriedJointReturn", number: 5, textKey: "q5" },
  ];

  const STATUS_OPTIONS: {
    value: "A" | "B" | "C";
    titleKey: string;
    helpKey: string;
  }[] = [
    { value: "A", titleKey: "optionA", helpKey: "optionAHelp" },
    { value: "B", titleKey: "optionB", helpKey: "optionBHelp" },
    { value: "C", titleKey: "optionC", helpKey: "optionCHelp" },
  ];

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<StudentDetailsForm>({
    resolver: yupResolver(schema) as any,
    mode: "onChange",
    defaultValues: defaultValues || {
      householdStatus: "",
      q1_titleIV: "",
      q2_fosterCare: "",
      q3_jtpa: "",
      q4_singleParent: "",
      q5_marriedJointReturn: "",
      studentsPlanningToEnroll: "",
    },
  });

  const handleFormSubmit = (data: StudentDetailsForm) => {
    onSubmit(data);
  };

  const householdStatus = watch("householdStatus");
  const showExemptions = householdStatus === "C";

  // When C is selected, collapse the A/B/C cards into a compact summary so
  // the screen stays manageable while the 5 questions are visible.
  // The user can re-open the chooser via the Change button.
  const [isEditingChoice, setIsEditingChoice] = useState(false);
  const collapseChoices = showExemptions && !isEditingChoice;

  // Reset the editing flag any time the selection moves away from C
  useEffect(() => {
    if (householdStatus !== "C") setIsEditingChoice(false);
  }, [householdStatus]);

  const selectedOption = STATUS_OPTIONS.find(
    (o) => o.value === householdStatus
  );

  const watchedAnswers = watch([
    "q1_titleIV",
    "q2_fosterCare",
    "q3_jtpa",
    "q4_singleParent",
    "q5_marriedJointReturn",
  ]);
  const allAnswered = watchedAnswers.every((v) => v === "yes" || v === "no");
  const allNo = allAnswered && watchedAnswers.every((v) => v === "no");
  const anyYes = watchedAnswers.some((v) => v === "yes");

  // ── Co-applicant read-only + acknowledgment view ──────────────────────────
  if (primaryStudentDetails) {
    const primary = primaryStudentDetails;
    const selectedLabel = STATUS_OPTIONS.find(
      (o) => o.value === primary.householdStatus
    );
    const yesNoLabel = (v: string) =>
      v === "yes"
        ? t("affordable.application.studentDetails.yes")
        : t("affordable.application.studentDetails.no");

    return (
      <Box>
        <RegisterReviewSection>
          <RegisterReviewSectionTitle>
            {t("affordable.application.studentDetails.title")}
          </RegisterReviewSectionTitle>

          <Alert severity="info" sx={{ mb: 3 }}>
            {t("affordable.application.studentDetails.primaryAnswerNote")}
          </Alert>

          {/* Primary's selected option — read-only */}
          <Box
            sx={{
              border: `1px solid ${COLORS.darkBlue[500]}`,
              backgroundColor: COLORS.blue[50],
              borderRadius: 2,
              p: 2,
              mb: primary.householdStatus === "C" ? 3 : 0,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.5, fontWeight: 600 }}
            >
              {t("affordable.application.studentDetails.selected")}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {selectedLabel
                ? t(
                    `affordable.application.studentDetails.${selectedLabel.titleKey}`
                  )
                : primary.householdStatus}
            </Typography>
          </Box>

          {/* If C, show the exemption answers read-only */}
          {primary.householdStatus === "C" && (
            <Box>
              {EXEMPTION_QUESTIONS.map((q) => (
                <Box
                  key={q.key}
                  sx={{
                    py: 1.5,
                    borderBottom: `1px solid ${COLORS.gray[100]}`,
                    "&:last-of-type": { borderBottom: "none" },
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.darkBlue[500],
                      minWidth: 24,
                    }}
                  >
                    {q.number}.
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t(`affordable.application.studentDetails.${q.textKey}`)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    >
                      {yesNoLabel(primary[q.key] as string)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {primary.studentsPlanningToEnroll && (
            <Box sx={{ mt: primary.householdStatus === "C" ? 3 : 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 600 }}
              >
                {t("affordable.application.studentDetails.planningQuestion")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {yesNoLabel(primary.studentsPlanningToEnroll)}
              </Typography>
            </Box>
          )}
        </RegisterReviewSection>

        {/* Acknowledgment */}
        <RegisterReviewSection>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {t("affordable.application.studentDetails.acknowledgeLabel")}
              </Typography>
            }
            sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0 } }}
          />
          {!acknowledged && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", mt: 0.5 }}
            >
              {t("affordable.application.studentDetails.acknowledgeRequired")}
            </Typography>
          )}
        </RegisterReviewSection>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            mb: 4,
          }}
        >
          <Button variant="outlined" onClick={onBack} disabled={isSaving}>
            {t("account.leaseApplication.previous")}
          </Button>
          <Button
            variant="contained"
            disabled={!acknowledged || isSaving}
            startIcon={
              isSaving ? <CircularProgress size={16} color="inherit" /> : null
            }
            onClick={() =>
              onSubmit({ ...primary, _acknowledgedAt: Date.now() })
            }
          >
            {t("account.leaseApplication.next")}
          </Button>
        </Box>
      </Box>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.studentDetails.title")}
        </RegisterReviewSectionTitle>
        {!collapseChoices && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("affordable.application.studentDetails.subtitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t("affordable.application.studentDetails.studentDefinition")}
            </Typography>
          </>
        )}

        {collapseChoices && selectedOption ? (
          <Box
            sx={{
              border: `1px solid ${COLORS.darkBlue[500]}`,
              backgroundColor: COLORS.blue[50],
              borderRadius: 2,
              p: 2,
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5, fontWeight: 600 }}
              >
                {t("affordable.application.studentDetails.selected")}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t(
                  `affordable.application.studentDetails.${selectedOption.titleKey}`
                )}
              </Typography>
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={() => setIsEditingChoice(true)}
              sx={{ flexShrink: 0 }}
            >
              {t("affordable.application.studentDetails.change")}
            </Button>
          </Box>
        ) : (
          <Controller
            name="householdStatus"
            control={control}
            render={({ field }) => (
              <FormControl
                component="fieldset"
                fullWidth
                error={!!errors.householdStatus}
              >
                <RadioGroup
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    setIsEditingChoice(false);
                  }}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <Box
                      key={opt.value}
                      sx={{
                        border: `1px solid ${
                          field.value === opt.value
                            ? COLORS.darkBlue[500]
                            : COLORS.gray[200]
                        }`,
                        borderRadius: 2,
                        p: 2,
                        mb: 1.5,
                        backgroundColor:
                          field.value === opt.value
                            ? COLORS.blue[50]
                            : "transparent",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <FormControlLabel
                        value={opt.value}
                        control={<Radio />}
                        sx={{
                          alignItems: "flex-start",
                          m: 0,
                          "& .MuiFormControlLabel-label": { flex: 1, mt: 0.5 },
                        }}
                        label={
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {t(
                                `affordable.application.studentDetails.${opt.titleKey}`
                              )}
                            </Typography>
                            {field.value === opt.value &&
                              t(
                                `affordable.application.studentDetails.${opt.helpKey}`
                              ) && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {t(
                                    `affordable.application.studentDetails.${opt.helpKey}`
                                  )}
                                </Typography>
                              )}
                          </Box>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
                {errors.householdStatus && (
                  <FormHelperText>
                    {errors.householdStatus.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        )}
      </RegisterReviewSection>

      {showExemptions && (
        <RegisterReviewSection>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("affordable.application.studentDetails.exemptionsSubtitle")}
          </Typography>

          {EXEMPTION_QUESTIONS.map((q) => (
            <Box
              key={q.key}
              sx={{
                py: 2,
                borderBottom: `1px solid ${COLORS.gray[100]}`,
                "&:last-of-type": { borderBottom: "none" },
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 700,
                    color: COLORS.darkBlue[500],
                    minWidth: 24,
                  }}
                >
                  {q.number}.
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 1.5 }}>
                    {t(`affordable.application.studentDetails.${q.textKey}`)}
                  </Typography>
                  <Controller
                    name={q.key}
                    control={control}
                    render={({ field }) => (
                      <FormControl error={!!errors[q.key]}>
                        <RadioGroup row {...field}>
                          <FormControlLabel
                            value="yes"
                            control={<Radio size="small" />}
                            label={t(
                              "affordable.application.studentDetails.yes"
                            )}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio size="small" />}
                            label={t(
                              "affordable.application.studentDetails.no"
                            )}
                            sx={{ ml: 2 }}
                          />
                        </RadioGroup>
                        {errors[q.key] && (
                          <FormHelperText>
                            {errors[q.key]?.message as string}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>
            </Box>
          ))}

          {allAnswered && anyYes && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>
                  {t("affordable.application.studentDetails.eligibleTitle")}
                </strong>{" "}
                {t("affordable.application.studentDetails.eligibleMessage")}
              </Typography>
            </Alert>
          )}
          {allNo && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>
                  {t("affordable.application.studentDetails.ineligibleTitle")}
                </strong>{" "}
                {t("affordable.application.studentDetails.ineligibleMessage")}
              </Typography>
              {(propertyContact?.phone || propertyContact?.email) && (
                <Box sx={{ mt: 1.5 }}>
                  {propertyContact?.phone && (
                    <Typography variant="body2">
                      {t("affordable.application.studentDetails.callUs")}:{" "}
                      <a
                        href={`tel:${propertyContact.phone}`}
                        style={{ color: theme.palette.primary.main }}
                      >
                        {propertyContact.phone}
                      </a>
                    </Typography>
                  )}
                  {propertyContact?.email && (
                    <Typography variant="body2">
                      {t("affordable.application.studentDetails.emailUs")}:{" "}
                      <a
                        href={`mailto:${propertyContact.email}`}
                        style={{ color: theme.palette.primary.main }}
                      >
                        {propertyContact.email}
                      </a>
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </RegisterReviewSection>
      )}

      <RegisterReviewSection>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
          {t("affordable.application.studentDetails.planningQuestion")}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          {t("affordable.application.studentDetails.planningHelp")}
        </Typography>
        <Controller
          name="studentsPlanningToEnroll"
          control={control}
          render={({ field }) => (
            <FormControl error={!!errors.studentsPlanningToEnroll}>
              <RadioGroup row {...field}>
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" />}
                  label={t("affordable.application.studentDetails.yes")}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={t("affordable.application.studentDetails.no")}
                  sx={{ ml: 2 }}
                />
              </RadioGroup>
              {errors.studentsPlanningToEnroll && (
                <FormHelperText>
                  {errors.studentsPlanningToEnroll.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      </RegisterReviewSection>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          mb: 4,
        }}
      >
        <Button variant="outlined" onClick={onBack} disabled={isSaving}>
          {t("account.leaseApplication.previous")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving || !isValid}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
